import { Fragment } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import { envConfigs } from '@/config';
import { pageHead } from '@/lib/page-head';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { Footer } from '@/blocks/footer';
import { Header } from '@/blocks/header';
import { NativeAd } from '@/components/analytics/banner-ad';
import { BlogCard } from '@/components/blog-card';
import { formatPostDate } from '@/content/posts';
import { getBlogPostsFn } from '@/content/posts/server';

export const Route = createFileRoute('/blog/')({
  loader: async () => {
    const locale = getLocale();
    const posts = await getBlogPostsFn({ data: { locale } });
    return { locale, posts };
  },
  head: ({ loaderData }) => {
    const locale = loaderData?.locale;
    const hasPublishedPosts = (loaderData?.posts.length ?? 0) > 0;
    const head = pageHead({
      path: '/blog',
      locale: locale ?? 'en',
      title: `${m['blog.title']({}, { locale })} | ${envConfigs.app_name}`,
      description: m['blog.description']({}, { locale }),
    });
    return {
      ...head,
      meta: [
        ...head.meta,
        ...(!hasPublishedPosts
          ? [{ name: 'robots', content: 'noindex,follow' }]
          : []),
      ],
    };
  },
  component: BlogPage,
});

function BlogPage() {
  const { locale, posts } = Route.useLoaderData();

  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-16 text-center">
            <h1 className="font-serif text-4xl font-normal tracking-tight sm:text-5xl">
              {m['blog.title']()}
            </h1>
            <p className="text-muted-foreground mx-auto mt-5 max-w-lg">
              {m['blog.description']()}
            </p>
          </div>
          {posts.length === 0 ? (
            <p className="text-muted-foreground text-center">
              {m['blog.no_posts']()}
            </p>
          ) : (
            <>
              {/* Native ad after the first two rows of cards. */}
              {[posts.slice(0, 6), posts.slice(6)].map((group, gi) => (
                <Fragment key={gi}>
                  {gi === 1 && <NativeAd className="my-10" />}
                  {group.length > 0 && (
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {group.map((post) => (
                        <BlogCard
                          key={post.slug}
                          href={`/blog/${post.slug}`}
                          title={post.title}
                          description={post.description}
                          image={post.cover ?? post.image}
                          date={formatPostDate(post.createdAt, locale)}
                          authorName={post.authorName}
                          authorImage={post.authorImage}
                        />
                      ))}
                    </div>
                  )}
                </Fragment>
              ))}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
